/**
 * Services Section Module
 * 3D card scroll animation for services slides
 * Hero animation (like about page)
 */
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Services Hero animation - similar to About page
 * Phase 1: Image moves to center
 * Phase 2: Image scales to fullscreen, overlay text appears
 */
export function initServicesHero() {
    const hero = document.querySelector('.services-hero');
    if (!hero) return;

    // Elements
    const imgWrapper = hero.querySelector('.services-hero-img-wrapper');
    const heroContent = hero.querySelector('.services-hero-col--content');
    const pageTitle = hero.querySelector('.page-title');
    const overlayText = hero.querySelector('.services-hero-overlay-text');
    const img = imgWrapper?.querySelector('img');

    if (imgWrapper && img) {
        // Get initial position from layout
        const rect = imgWrapper.getBoundingClientRect();
        const startWidth = rect.width;
        const startHeight = rect.height;

        // Starting position (desktop: 25% left, 35% top)
        const isMobile = window.innerWidth < 992;
        const startLeft = isMobile ? '50%' : '17%';
        const startTop = isMobile ? '100%' : '45%';
        const startXPercent = isMobile ? -50 : 0;
        const startYPercent = isMobile ? -50 : 0;

        // Passepartout: 1rem = 16px
        const passepartout = 16;

        // Final dimensions
        const finalWidth = window.innerWidth - passepartout * 2;
        const finalHeight = window.innerHeight - passepartout * 2;

        // Calculate scale factors
        const scaleX = finalWidth / startWidth;
        const scaleY = finalHeight / startHeight;
        const scaleFactor = Math.max(scaleX, scaleY);

        // Create timeline with two phases
        // refreshPriority ensures this is calculated before other ScrollTriggers
        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: hero,
                start: 'top top',
                end: '+=200%',
                scrub: 1.5,
                pin: true,
                pinSpacing: true,
                refreshPriority: 10, // Higher priority = calculated first
                invalidateOnRefresh: true
            }
        });

        // Set initial state with GPU acceleration
        gsap.set(imgWrapper, {
            position: 'fixed',
            left: startLeft,
            top: startTop,
            xPercent: startXPercent,
            yPercent: startYPercent,
            width: startWidth,
            height: startHeight,
            willChange: 'transform',
            force3D: true,
            backfaceVisibility: 'hidden'
        });

        // Phase 1 (0 to 0.35): Move to center at same size
        tl.to(imgWrapper, {
            left: '50%',
            top: '50%',
            xPercent: -50,
            yPercent: -50,
            duration: 0.35,
            ease: 'power2.out'
        }, 0);

        // Fade out content
        if (heroContent) {
            tl.to(heroContent, {
                opacity: 0,
                x: 50,
                duration: 0.2
            }, 0);
        }

        // Fade out page title
        if (pageTitle) {
            tl.to(pageTitle, {
                opacity: 0,
                y: -30,
                duration: 0.2
            }, 0);
        }

        // Phase 2 (0.35 to 0.8): Scale up from center (GPU accelerated)
        tl.to(imgWrapper, {
            scale: scaleFactor,
            borderRadius: '0px',
            duration: 0.45,
            ease: 'power2.inOut'
        }, 0.35);

        // Show overlay text
        if (overlayText) {
            tl.to(overlayText, {
                opacity: 1,
                duration: 0.15
            }, 0.3);

            tl.to(overlayText, {
                opacity: 0,
                duration: 0.15
            }, 0.65);
        }

        // Add subtle blur
        tl.to(img, {
            filter: 'blur(2px)',
            duration: 0.3
        }, 0.4);
    }
}

export function initServicesSection() {
    const section = document.querySelector('.services-section');
    const scrollHint = document.querySelector('.services-scroll-hint');
    const slides = document.querySelectorAll('.services-slide');

    if (!section || slides.length === 0) {
        return;
    }

    // Hide scroll hint when entering section
    if (scrollHint) {
        gsap.to(scrollHint, {
            autoAlpha: 0,
            duration: 0.2,
            scrollTrigger: {
                trigger: section,
                start: 'top top',
                end: 'top top-=1',
                toggleActions: 'play none reverse none'
            }
        });
    }

    // Animate each slide
    slides.forEach((slide) => {
        const contentWrapper = slide.querySelector('.services-content-wrapper');
        const content = slide.querySelector('.services-content');

        if (!contentWrapper || !content) return;

        // 3D rotation and scale animation
        gsap.to(content, {
            rotationZ: (Math.random() - 0.5) * 10, // Random rotation between -5 and 5 degrees
            scale: 0.7,
            rotationX: 40,
            ease: 'power1.in',
            scrollTrigger: {
                pin: contentWrapper,
                trigger: slide,
                start: 'top 0%',
                end: '+=' + window.innerHeight,
                scrub: true
            }
        });

        // Fade out animation
        gsap.to(content, {
            autoAlpha: 0,
            ease: 'power1.in',
            scrollTrigger: {
                trigger: content,
                start: 'top -80%',
                end: '+=' + 0.2 * window.innerHeight,
                scrub: true
            }
        });
    });
}

/**
 * Services Text Reveal animation
 * Text words color from gray to primary as user scrolls
 * Section is pinned while text reveals
 */
export function initServicesTextReveal() {
    const section = document.querySelector('.services-text-reveal');
    if (!section) return;

    const textElement = section.querySelector('.services-text-reveal__text');
    if (!textElement) return;

    // Wrap each word in a span
    const text = textElement.textContent.trim();
    textElement.innerHTML = text
        .split(/\s+/)
        .filter(word => word.trim() !== '')
        .map(word => `<span class="word">${word}</span>`)
        .join(' ');

    const words = textElement.querySelectorAll('.word');
    const totalWords = words.length;

    // Create ScrollTrigger with pin for the section
    ScrollTrigger.create({
        trigger: section,
        start: 'top top',
        end: '+=100%', // Pin for one full viewport height of scrolling
        pin: true,
        pinSpacing: true,
        scrub: 1,
        onUpdate: (self) => {
            const progress = self.progress;
            // Add 1 to include the last word when progress reaches 1
            const wordsToShow = Math.ceil(progress * totalWords);

            words.forEach((word, index) => {
                if (index < wordsToShow) {
                    word.classList.add('is-visible');
                } else {
                    word.classList.remove('is-visible');
                }
            });
        }
    });
}
